import React, { useMemo, useState, useEffect, useCallback } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import { get, post } from "../utils/api";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  User,
  DollarSign,
  Calendar,
  Hash,
  FileText,
  ArrowRight,
  ShieldCheck,
  LayoutList,
  Loader2,
  AlertCircle
} from "lucide-react";

const TransactionApprovalPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
  const amc_name = userDetails?.username || userDetails?.user_name;
  const user_Id = userDetails?.id || userDetails?.user_id;

  const fetchReleasedTransactions = useCallback(async () => {
    if (!amc_name) return;
    try {
      setLoading(true);
      const response = await post("/transactionmanagement/fetchTransactionApproval", { checker_name: amc_name });

      const apiData = response?.data || response;
      if (apiData?.Success && apiData?.Data) {
        const mappedData = apiData.Data.map((item) => ({
          id: item.id,
          transactionDate: item.created_date || item.createdat,
          processMonth: item.process_month,
          clientName: item.client_name,
          pan: item.client_pan,
          folioNo: item.application_number,
          application_number: item.application_number,
          distributorCode: item.arn_code,
          distributorPan: item.distributor_pan,
          productCode: item.class_code,
          capitalCommitment: Number(item.capital_commitment) || 0,
          placementFeePercent: Number(item.placement_fee_per) || 0,
          placementFeeAmount: Number(item.placement_fee_amount) || 0,
          upfrontFeePercent: Number(item.upfront_fee_per) || 0,
          upfrontFeeAmount: Number(item.upfront_fee_amount) || 0,
          trailPercent: Number(item.trail_per) || 0,
          totalTrailAmount: Number(item.total_trail_amount) || 0,
          status: item.is_approved === true ? "approved" : item.is_rejected === true ? "rejected" : "pending",
          remarks: item.remarks || "",
          checker_name: item.checker_name,
        }));
        setTransactions(mappedData);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  }, [amc_name]);

  useEffect(() => {
    fetchReleasedTransactions();
  }, [fetchReleasedTransactions]);

  const handleAction = async (id, action) => {
    const transaction = transactions.find(t => String(t.id) === String(id));
    if (!transaction || !user_Id) return;

    const confirmMsg = `Are you sure you want to ${action} this transaction?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setLoading(true);
      const endpoint = action === 'approve'
        ? "/transactionmanagement/approveTransaction"
        : "/transactionmanagement/rejectTransaction";

      const response = await post(endpoint, {
        id: id,
        application_number: transaction.application_number,
        user_Id: user_Id
      });

      const apiData = response?.data || response;
      if (apiData?.Success) {
        alert(`Transaction ${action}d successfully!`);
        fetchReleasedTransactions();
      } else {
        alert(`Failed to ${action}: ${apiData?.Message || "Unknown error"}`);
      }
    } catch (error) {
      console.error(`Error during ${action}:`, error);
      alert(`Failed to ${action} transaction.`);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      Header: <div className="flex items-center gap-2"><Calendar size={14} /> DATE</div>,
      accessor: "transactionDate",
      size: "120px",
      Cell: ({ value }) => {
        if (!value) return "-";
        const date = new Date(value);
        return <span className="font-bold text-gray-600">{date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}</span>;
      }
    },
    {
      Header: <div className="flex items-center gap-2"><FileText size={14} /> CLIENT</div>,
      accessor: "clientName",
      size: "180px",
      Cell: ({ value, row }) => (
        <div className="flex flex-col">
          <span className="font-black text-gray-900 uppercase text-[11px] truncate">{value}</span>
          <span className="text-[10px] text-gray-400 font-bold">{row.original.pan}</span>
        </div>
      )
    },
    {
      Header: <div className="flex items-center gap-2"><Hash size={14} /> FOLIO</div>,
      accessor: "folioNo",
      size: "120px",
      Cell: ({ value }) => <span className="font-black text-indigo-600 tracking-wider">{value}</span>
    },
    {
      Header: <div className="flex items-center gap-2"><DollarSign size={14} /> COMMITMENT</div>,
      accessor: "capitalCommitment",
      size: "140px",
      Cell: ({ value }) => <span className="font-black text-gray-800">₹{value.toLocaleString()}</span>
    },
    {
      Header: "FEES (%)",
      accessor: "placementFeePercent",
      size: "120px",
      Cell: ({ row }) => (
        <div className="flex flex-col text-[10px] font-bold">
          <span className="text-indigo-600">P: {row.original.placementFeePercent}%</span>
          <span className="text-emerald-600">U: {row.original.upfrontFeePercent}%</span>
          <span className="text-amber-600">T: {row.original.trailPercent}%</span>
        </div>
      )
    },
    {
      Header: "STATUS",
      accessor: "status",
      size: "140px",
      Cell: ({ value }) => {
        const styles = {
          approved: "text-emerald-600 bg-emerald-50 border-emerald-100",
          rejected: "text-rose-600 bg-rose-50 border-rose-100",
          pending: "text-amber-600 bg-amber-50 border-amber-100"
        };
        const icons = {
          approved: <CheckCircle2 size={12} />,
          rejected: <XCircle size={12} />,
          pending: <Clock size={12} />
        };
        return (
          <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border flex items-center gap-1.5 w-fit ${styles[value] || styles.pending}`}>
            {icons[value] || icons.pending} {value || "pending"}
          </span>
        );
      }
    },
    {
      Header: "ACTIONS",
      size: "220px",
      Cell: ({ row }) => {
        const isPending = row.original.status === "pending";
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleAction(row.original.id, 'approve')}
              disabled={!isPending || loading}
              className={`h-[32px] px-4 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1.5 ${isPending ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm active:scale-95" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
            >
              <CheckCircle2 size={14} /> Approve
            </button>
            <button
              onClick={() => handleAction(row.original.id, 'reject')}
              disabled={!isPending || loading}
              className={`h-[32px] px-4 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1.5 ${isPending ? "bg-rose-600 text-white hover:bg-rose-700 shadow-sm active:scale-95" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
            >
              <XCircle size={14} /> Reject
            </button>
          </div>
        );
      }
    }
  ], [loading]);

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100">
            <ShieldCheck size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 tracking-tight uppercase">Transaction Approval</h1>
            <p className="text-gray-500 text-[11px] font-bold uppercase tracking-widest italic flex items-center gap-1">
              Review and authorize pending disbursements <ArrowRight size={10} />
            </p>
          </div>
        </div>

        <button
          onClick={fetchReleasedTransactions}
          disabled={loading}
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 h-[42px] px-6 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95"
        >
          <RefreshCw size={16} className={loading ? "animate-spin text-indigo-500" : "text-indigo-600"} />
          Refresh Registry
        </button>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-1">
          {loading && transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4 text-gray-300">
              <Loader2 size={48} className="animate-spin text-indigo-500" />
              <span className="font-black text-sm tracking-[0.2em] uppercase">Auth-Sync in progress...</span>
            </div>
          ) : transactions.length > 0 ? (
            <ReusableTable columns={columns} data={transactions} />
          ) : (
            <div className="flex flex-col items-center justify-center py-32 gap-4 text-gray-400">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                <CheckCircle2 size={32} className="text-gray-200" />
              </div>
              <div className="text-center">
                <p className="font-black text-sm uppercase tracking-widest">All Clear</p>
                <p className="text-[11px] font-medium italic mt-1">No pending transactions requiring your approval.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionApprovalPage;